import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["linesBody", "jsonField", "discountField", "applyDiscount", "advancedToggle", "warehouseItem", "warehouseAdd"]
  static values = {
    defaultUnit: String,
    defaultVatCategory: String,
    defaultVatRate: Number,
    unitCodeLabel: String,
    vatCategoryLabel: String,
    vatRateReasonLabel: String,
    vatExemptionReason: String,
    manualSource: String,
    warehouseSource: String,
    removeLineLabel: String
  }

  connect() {
    this.toggleDiscount()
    this._onInput = (event) => {
      if (event.target.matches(".line-qty, .line-price")) {
        this.recalcRow(event.target.closest("tr.invoice-line"))
      }
    }
    this.linesBodyTarget.addEventListener("input", this._onInput)
    this.toggleWarehouseAdd()
  }

  disconnect() {
    this.linesBodyTarget.removeEventListener("input", this._onInput)
  }

  toggleDiscount() {
    this.discountFieldTarget.style.display = this.applyDiscountTarget.checked ? "" : "none"
  }

  toggleAdvanced(event) {
    event.preventDefault()
    const visible = !this.element.classList.contains("invoice-editor--advanced")
    this.element.classList.toggle("invoice-editor--advanced", visible)
    this.advancedToggleTarget.setAttribute("aria-expanded", String(visible))
  }

  addLine(event) {
    event.preventDefault()
    this.linesBodyTarget.appendChild(this.buildRow())
  }

  addWarehouseLine(event) {
    event.preventDefault()
    const option = this.warehouseItemTarget.selectedOptions[0]
    if (!option?.value) return

    const row = this.buildRow()
    row.dataset.sourceType = "part"
    row.dataset.unitCost = option.dataset.cost || "0"
    row.dataset.warehouseItemId = option.value
    row.querySelector(".line-type").value = option.dataset.category || ""
    row.querySelector(".line-desc").value = option.dataset.name || ""
    row.querySelector(".line-unit").value = option.dataset.unit || this.defaultUnitValue
    row.querySelector(".line-unit-code").value = option.dataset.unitCode || "C62"
    row.querySelector(".line-price").value = option.dataset.price || "0"
    row.querySelector(".line-qty").max = option.dataset.available || ""
    this.setSourceBadge(row, "warehouse", this.warehouseSourceValue)
    this.recalcRow(row)
    this.linesBodyTarget.appendChild(row)
    this.warehouseItemTarget.value = ""
    this.toggleWarehouseAdd()
    row.querySelector(".line-qty").focus()
  }

  toggleWarehouseAdd() {
    if (!this.hasWarehouseItemTarget || !this.hasWarehouseAddTarget) return

    this.warehouseAddTarget.disabled = !this.warehouseItemTarget.value
  }

  removeLine(event) {
    event.preventDefault()
    event.target.closest("tr").remove()
  }

  serialize() {
    const rows = this.linesBodyTarget.querySelectorAll("tr.invoice-line")
    const lines = Array.from(rows).map(row => {
      const qty   = parseFloat(row.querySelector(".line-qty").value) || 0
      const price = parseFloat(row.querySelector(".line-price").value) || 0
      const unitCost = parseFloat(row.dataset.unitCost) || 0
      return {
        task_type: row.querySelector(".line-type").value,
        description: row.querySelector(".line-desc").value,
        quantity: qty,
        unit: row.querySelector(".line-unit").value,
        unit_code: row.querySelector(".line-unit-code").value,
        unit_price: price,
        total: parseFloat((qty * price).toFixed(2)),
        source_type: row.dataset.sourceType || "manual",
        unit_cost: unitCost,
        cost_total: parseFloat((qty * unitCost).toFixed(2)),
        warehouse_item_id: row.dataset.warehouseItemId || null,
        vat_category_code: row.querySelector(".line-vat-category").value,
        vat_rate: parseFloat(row.querySelector(".line-vat-rate").value) || 0,
        vat_exemption_reason: row.querySelector(".line-vat-reason").value
      }
    })
    this.jsonFieldTarget.value = JSON.stringify(lines)
  }

  recalcRow(row) {
    if (!row) return
    const qty = parseFloat(row.querySelector(".line-qty").value) || 0
    const price = parseFloat(row.querySelector(".line-price").value) || 0
    row.querySelector(".line-total").textContent = (qty * price).toFixed(2)
  }

  setSourceBadge(row, source, label) {
    const badge = row.querySelector(".invoice-line__source")
    badge.className = `invoice-line__source invoice-line__source--${source}`
    badge.querySelector("i").className = source === "warehouse" ? "bi bi-box-seam" : "bi bi-pencil"
    badge.querySelector(".invoice-line-source-label").textContent = label
  }

  buildRow() {
    const tr = document.createElement("tr")
    tr.className = "invoice-line"
    tr.dataset.sourceType = "manual"
    tr.dataset.unitCost = "0"
    tr.innerHTML = `
      <td>
        <span class="invoice-line__source invoice-line__source--manual">
          <i class="bi bi-pencil"></i>
          <span class="invoice-line-source-label">${this.manualSourceValue}</span>
        </span>
        <input type="text" class="form-control form-control-sm line-type">
      </td>
      <td>
        <input type="text" class="form-control form-control-sm line-desc">
        <div class="e-invoice-line-settings mt-2 p-2 bg-light border rounded-2">
          <div class="row g-2">
            <div class="col-12 col-lg-3">
              <label class="form-label small">${this.unitCodeLabelValue}</label>
              <select class="form-select form-select-sm line-unit-code"><option value="C62">C62</option><option value="DAY">DAY</option><option value="GRM">GRM</option><option value="HUR">HUR</option><option value="KGM">KGM</option><option value="LTR">LTR</option><option value="SET">SET</option></select>
            </div>
            <div class="col-12 col-lg-3">
              <label class="form-label small">${this.vatCategoryLabelValue}</label>
              <select class="form-select form-select-sm line-vat-category"><option value="S" ${this.defaultVatCategoryValue === "S" ? "selected" : ""}>S</option><option value="Z" ${this.defaultVatCategoryValue === "Z" ? "selected" : ""}>Z</option><option value="E" ${this.defaultVatCategoryValue === "E" ? "selected" : ""}>E</option><option value="O" ${this.defaultVatCategoryValue === "O" ? "selected" : ""}>O</option></select>
            </div>
            <div class="col-12 col-lg-6">
              <label class="form-label small">${this.vatRateReasonLabelValue}</label>
              <div class="input-group input-group-sm"><input type="number" class="form-control line-vat-rate" step="0.01" min="0" value="${this.defaultVatRateValue.toFixed(2)}"><span class="input-group-text">%</span></div>
              <input type="text" class="form-control form-control-sm mt-1 line-vat-reason" placeholder="${this.vatExemptionReasonValue}">
            </div>
          </div>
        </div>
      </td>
      <td><input type="number" class="form-control form-control-sm line-qty" step="0.01" value="1"></td>
      <td><input type="text" class="form-control form-control-sm line-unit" value="${this.defaultUnitValue}"></td>
      <td><input type="number" class="form-control form-control-sm line-price" step="0.01" value="0.00"></td>
      <td class="text-end line-total">0.00</td>
      <td>
        <button type="button" class="btn btn-sm btn-danger invoice-line__remove" data-action="click->invoice-editor#removeLine">
          <i class="bi bi-trash3"></i>
        </button>
      </td>`
    const removeButton = tr.querySelector(".invoice-line__remove")
    removeButton.title = this.removeLineLabelValue
    removeButton.setAttribute("aria-label", this.removeLineLabelValue)
    return tr
  }
}
